import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, testConnection } from '../lib/supabase';
import type { Company } from '../types';

interface CompanyContextType {
  company: Company;
  setCompany: (company: Company) => void;
  companies: Company[];
  loading: boolean;
  error: string | null;
  retry: () => Promise<void>;
}

const defaultCompany: Company = {
  id: 'f8c3de3d-1fea-4d7c-a8b0-29f63c4c3454',
  name: 'Zumm Delivery',
  logo: 'https://zummdelivery.com.br/wp-content/uploads/2023/01/admin-ajax-1.png',
  primaryColor: '#2563eb',
  secondaryColor: '#1e40af',
  active: true,
  createdAt: new Date().toISOString(),
};

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export function CompanyProvider({ children }: { children: React.ReactNode }) {
  const [company, setCompany] = useState<Company>(defaultCompany);
  const [companiesList, setCompaniesList] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError(null);

      // Test connection first
      const isConnected = await testConnection();
      if (!isConnected) {
        throw new Error('Não foi possível conectar ao servidor. Por favor, tente novamente.');
      }
      
      const { data: companies, error: supabaseError } = await supabase
        .from('companies')
        .select('*')
        .eq('active', true);

      if (supabaseError) throw supabaseError;

      const validCompanies = Array.isArray(companies) ? companies : [];
      setCompaniesList(validCompanies.length > 0 ? validCompanies : [defaultCompany]);
      
      const params = new URLSearchParams(window.location.search);
      const brandId = params.get('brand');
      
      if (brandId) {
        const brandCompany = validCompanies.find(c => c.id === brandId);
        if (brandCompany) {
          setCompany(brandCompany);
        }
      } else {
        const defaultCompanyData = validCompanies.find(c => c.id === defaultCompany.id);
        if (defaultCompanyData) {
          setCompany(defaultCompanyData);
        }
      }
    } catch (err: any) {
      console.error('Erro ao carregar empresas:', err.message);
      setError(err.message || 'Erro ao carregar empresas. Por favor, tente novamente.');
      setCompaniesList([defaultCompany]);
      
      // Retry logic
      if (retryCount < 3) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          loadCompanies();
        }, 2000 * (retryCount + 1)); // Exponential backoff
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  return (
    <CompanyContext.Provider value={{
      company,
      setCompany,
      companies: companiesList,
      loading,
      error,
      retry: loadCompanies
    }}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}